/* global ga */

import moment from 'moment-timezone';
import { Manager, Swipe } from 'hammerjs';
import Raven from 'raven-js';

import { createEl, toggle, removeClass, scrollTo } from '../../helpers/domHelpers';
import { setSecondaryColor } from '../../helpers/setColors';
import { onAppointmentClick } from '../../helpers/analytics';
import { widgetCtrl as ctrl } from '../../widgetController';
import widgetModal from '../widgetModal';
import locations from './locations';
import styles from '../../css/calendar.css';

export const findTimeText = (automation, startDateTime) => {
  const timeString = moment.tz(startDateTime, ctrl.getTz()).format('h:mm A');
  if (!automation) return timeString;
  if (timeString === '9:30 AM') { return `Late Morning`; }
  if (timeString === '12:00 PM') { return 'Early Afternoon'; }
  if (timeString === '2:30 PM') { return 'Late Afternoon'; }
  return `Early Morning`;
}

const calendar = {

  init() {
    this.calendarContainer = createEl('div', styles.calendar);
    this.initWeek();
    this.initTimes();
    this.timesInstruction = createEl('div', styles.timesInstruction);

    const dateTimeLabel = createEl('span', styles.dateTimeLabel);
    dateTimeLabel.textContent = 'choose date and time';
    // APPEND SPAN BEFORE weekContainer below
    this.calendarContainer
      .append(dateTimeLabel,
        this.weekContainer,
        this.timesInstruction,
        this.timesContainer,
        this.overlay,
      );

    return this.calendarContainer;
  },

  initTimes() {
    this.timesContainer = createEl('div', styles.timesContainer);
    this.times = createEl('div', styles.times);
    // TODO: add up/down arrows and configure their scolling behavior
    // const timesArrow = createEl('div', styles.arrow, 'keyboard_arrow_down');
    this.timesContainer.append(this.times);
    this.overlay = createEl('div', styles.timesOverlay);
  },

  initWeek() {
    this.weekStart = moment().tz(ctrl.getTz()).startOf('week');
    this.weekContainer = createEl('div', styles.weekContainer);
    this.week = createEl('div', styles.week);

    // create calendar days:
    const calendarDay = this.weekStart.clone();
    const bookings = ctrl.getAllBookings();
    const lastAvailable = bookings.length ?
      moment.tz(bookings[bookings.length - 1].start_datetime, ctrl.getTz()).endOf('week') :
      moment().tz(ctrl.getTz()).endOf('week');

    while (calendarDay.isSameOrBefore(lastAvailable, 'day')) {
      const day = this.makeDay(calendarDay.clone());
      this.week.append(day);
      calendarDay.add(1, 'd');
    }

    const leftArrow = createEl('div', styles.arrow, 'keyboard_arrow_left');
    const rightArrow = createEl('div', styles.arrow, 'keyboard_arrow_right');
    this.weekContainer.append(leftArrow, this.week, rightArrow);

    // define arrow/swipe listeners
    this.toggleWeekArrows = () => {
      leftArrow.style.visibility = this.weekIndex > 0 ?
        'visible' :
        'hidden';
      rightArrow.style.visibility = (this.weekIndex + 1) * 7 === this.week.childNodes.length ?
        'hidden' :
        'visible';
    };

    const scrollCalendar = (direction) => {
      // Google Analytics:
      if (process.env.NODE_ENV === 'production') {
        ga('openchair.send', {
          hitType: 'event',
          eventCategory: 'Calendar',
          eventAction: 'scroll',
        });
      }
      this.removeActiveClass();
      const scrollLeft = this.week.clientWidth * this.weekIndex;
      if (direction === 'right' && (this.weekIndex + 1) * 7 < this.week.childNodes.length) {
        scrollTo(this.week, scrollLeft + this.week.clientWidth, 200);
        this.weekStart.add(1, 'week');
        this.weekIndex++;
      } else if (direction === 'left' && this.weekIndex > 0) {
        scrollTo(this.week, scrollLeft - this.week.clientWidth, 200);
        this.weekStart.subtract(1, 'week');
        this.weekIndex--;
      }
      // 13 is the max number of weeks we are showing to the user
      // getLoadStatus returns a negatice number that indicates how many weeks left to load
      if ((13 - this.weekIndex) + ctrl.getLoadStatus() <= 2) {
        this.loadMoreAppointments()
        .then(() => {
          this.findFirstAppointmentThisWeek();
          return this.renderTimes();
        })
        .then(() => {
          locations.render();
          this.toggleWeekArrows();
        }).catch((err) => {
          Raven.captureException(err);
        });
      } else {
        this.findFirstAppointmentThisWeek();
        this.renderTimes()
        .then(() => {
          locations.render();
          this.toggleWeekArrows();
        }).catch((err) => {
          Raven.captureException(err);
        });
      }
    };

    rightArrow.addEventListener('click', () => {
      scrollCalendar('right');
    });

    leftArrow.addEventListener('click', () => {
      scrollCalendar('left');
    });

    // for touch enabled devices:
    const touchableWeek = new Manager(this.week, { recognizers: [[Swipe]] });
    touchableWeek.on('swipeleft', () => {
      scrollCalendar('right');
    });
    touchableWeek.on('swiperight', () => {
      scrollCalendar('left');
    });
  },

  makeDay(date) {
    const day = createEl('div', styles.day);
    const dayOfWeek = createEl('div', styles.dayOfWeek, date.format('dd')[0]);
    const month = createEl('div', styles.month, date.format('MMM'));
    const dayOfMonth = createEl('div', styles.dayOfMonth);
    const innerDay = createEl('div', styles.dayOfMonthInner, date.format('D'), { name: 'openChairWidgetInnerDay' });
    dayOfMonth.append(innerDay);
    day.append(dayOfWeek, month, dayOfMonth);

    day.addEventListener('click', () => {
      // Google Analytics:
      if (process.env.NODE_ENV === 'production') {
        ga('openchair.send', {
          hitType: 'event',
          eventCategory: 'Calendar',
          eventAction: 'daySelect',
        });
      }

      this.selectDay(day, date);
      this.renderTimes();
    });
    return day;
  },

  markAvailableDays() {
    const days = Array.from(this.week.childNodes);
    const calendarDay = this.weekStart.clone().subtract(this.weekIndex, 'weeks');
    const bookings = ctrl.getCurrentLocation().bookings;
    let i = 0;
    let j = 0;
    while (i < days.length) {
      if (j >= bookings.length || calendarDay.isBefore(bookings[j].start_datetime, 'day')) {
        days[i].className = `${styles.day}  ${styles.isUnavailable}`;
        calendarDay.add(1, 'day');
        i++;
      } else if (calendarDay.isSame(bookings[j].start_datetime, 'day')) {
        removeClass(days[i], styles.isUnavailable);
        i++;
        j++;
        calendarDay.add(1, 'day');
      } else {
        j++;
      }
    }
  },

  removeActiveClass(clickedEl) {
    const lastActive = this.week.querySelector(`.${styles.isActive}`);
    if (lastActive) {
      const lastActiveStyle = lastActive.childNodes[2].childNodes[0].style;
      removeClass(lastActive, styles.isActive);
      if (clickedEl !== lastActive) {
        lastActiveStyle.removeProperty('background');
        lastActiveStyle.removeProperty('color');
      }
    }
  },

  selectDay(dayElem, date) {
    const dayOfMonthInner = dayElem.childNodes[2].childNodes[0];

    setSecondaryColor(dayOfMonthInner, styles.dayOfMonthInner);

    this.removeActiveClass(dayElem);
    this.timesContainer.scrollTop = 0;
    if (dayElem && date) {
      dayElem.className += `  ${styles.isActive}`;
      ctrl.setCurrentDay(date);
    } else {
      ctrl.setCurrentDay(null);
    }
  },

  findFirstAppointmentThisWeek() {
    const calendarDay = this.weekStart.clone();
    for (let i = 0; i < 7; i++) {
      const dayElem = this.week.childNodes[(this.weekIndex * 7) + calendarDay.day()];
      if (dayElem.className.split(' ').indexOf(styles.isUnavailable) < 0) {
        this.selectDay(dayElem, calendarDay.clone());
        return;
      }
      calendarDay.add(1, 'days');
    }
    // select monday unless it's the first (current week) - in that case select today
    this.weekIndex > 0 ?
      this.selectDay(this.week.childNodes[this.weekIndex * 7], this.weekStart.clone()) :
      this.selectDay(this.week.childNodes[moment().day()], moment());
  },

  loadMoreAppointments() {
    widgetModal.toggleSpinner();
    let bookings = ctrl.getAllBookings();
    const lastRendered = bookings.length ?
      moment.tz(bookings[bookings.length - 1].start_datetime, ctrl.getTz()).endOf('week').add(1, 'day') :
      moment().tz(ctrl.getTz()).endOf('week').add(1, 'day');
    return ctrl.loadNextPage()
      .then(() => {
        bookings = ctrl.getAllBookings();
        const lastAvailable = bookings.length ?
          moment.tz(bookings[bookings.length - 1].start_datetime, ctrl.getTz()).endOf('week') :
          moment.tz(ctrl.getTz()).endOf('week');

        while (lastRendered.isSameOrBefore(lastAvailable, 'day')) {
          const day = this.makeDay(lastRendered.clone());
          this.week.append(day);
          lastRendered.add(1, 'd');
        }
        this.markAvailableDays();
        widgetModal.toggleSpinner();
      }).catch((err) => {
        Raven.captureException(err);
      });
  },

  renderOverlay(timeframe, nextAvailable) {
    const message = createEl('p', styles.overlayText);
    if (nextAvailable) {
      this.overlayLink = createEl('span', styles.nextAppointmentLink, `${nextAvailable.format('ddd, MMM D')}`);
      message.textContent = 'Next available at this location: \n';
      message.append(
        this.overlayLink,
        createEl('span', styles.linkArrow, 'keyboard_arrow_right'),
      );

      this.overlayLink.addEventListener('click', (e) => {
        e.preventDefault();
        const weekDiff = nextAvailable.diff(this.weekStart, 'weeks');
        this.weekStart.add(weekDiff, 'weeks');
        this.weekIndex += weekDiff;
        scrollTo(this.week, (this.week.clientWidth * this.weekIndex), 200);
        setTimeout(() => {
          this.selectDay(this.week.childNodes[nextAvailable.day() + (this.weekIndex * 7)], nextAvailable);
          locations.render();
          this.renderTimes()
          .then(() => {
            this.toggleWeekArrows();
          });
        }, 200);
      });
    } else if (ctrl.getLoadStatus() < 1) {
      return this.loadMoreAppointments()
      .then(() => this.renderTimes())
      .catch((err) => {
        Raven.captureException(err);
      });
    } else {
      message.textContent = 'No future appoinments available\n for this location.';
    }
    // this.overlay.textContent = `No appointments available this ${timeframe}.`;
    this.overlay.innerHTML = '';
    this.overlay.append(message);

    toggle(this.timesContainer, this.overlay);
  },

  renderTimes() {
    const day = ctrl.getCurrentDay();
    const location = ctrl.getCurrentLocation();
    if (!day) {
      return Promise.resolve(this.renderOverlay('week', ctrl.findNextAvailableAppointment(this.weekStart)));
    }
    const timesElements = ctrl.getBookingsForDay(day, location.bookings)
      .filter((item, i, times) => {
        if (times[i + 1]) {
          return item.start_datetime !== times[i + 1].start_datetime;
        }
        return true;
      })
      .map((item) => {
        const timeText = findTimeText(item.automation, item.start_datetime);
        const el = createEl('div', styles.time, timeText);
        el.addEventListener('click', onAppointmentClick);
        el.addEventListener('click', () => widgetModal.onAppointmentTimeClick(item));
        return el;
      });
    this.timesInstruction.innerHTML = `${day.format('dddd, MMMM Do')} - ${location.name}`;
    if (timesElements.length) {
      this.times.innerHTML = '';
      this.times.append(...timesElements);
      toggle(this.overlay, this.timesContainer);
      return Promise.resolve(true);
    }
    return Promise.resolve(this.renderOverlay('day', ctrl.findNextAvailableAppointment(day)));
  },

  render() {
    let currentDay;
    if (!ctrl.getCurrentDay()) {
      currentDay = moment().tz(ctrl.getTz()).startOf('day');
      this.weekStart = moment().tz(ctrl.getTz()).startOf('week');
      this.weekIndex = 0;
      scrollTo(this.week, 0);
      ctrl.setCurrentDay(currentDay);
    } else {
      currentDay = ctrl.getCurrentDay();
    }
    this.toggleWeekArrows();
    this.markAvailableDays();
    this.renderTimes()
    .then(() => {
      this.selectDay(this.week.childNodes[currentDay.day() + (this.weekIndex * 7)], currentDay);
    });
  },
};

export default calendar;



// WEBPACK FOOTER //
// ./src/views/calendarView/calendar.js